import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, MapPin, Phone, Mail, Globe, Heart, Users as UsersIcon, Briefcase, Facebook, Instagram, Linkedin, Music2, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function MemberDirectory() {
  const [items, setItems] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (city) qs.set('city', city);
      if (specialty) qs.set('specialty', specialty);
      const { data } = await api.get(`/directory${qs.toString() ? `?${qs}` : ''}`);
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/specialties').then(({ data }) => setSpecs(data || [])).catch(() => {});
    api.get('/me/favorites').then(({ data }) => {
      setFavorites(new Set((data || []).map((u) => u.user_id)));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, city, specialty]);

  const cities = useMemo(() => Array.from(new Set(items.map((i) => i.city).filter(Boolean))), [items]);

  const toggleFav = async (uid) => {
    try {
      if (favorites.has(uid)) {
        await api.delete(`/me/favorites/${uid}`);
        const next = new Set(favorites); next.delete(uid); setFavorites(next);
        toast.success('Quitado de favoritos');
      } else {
        await api.post('/me/favorites', { target_user_id: uid });
        const next = new Set(favorites); next.add(uid); setFavorites(next);
        toast.success('Agregado a favoritos');
      }
    } catch (e) {
      toast.error('No se pudo actualizar favoritos');
    }
  };

  return (
    <div className="space-y-5" data-testid="member-directory">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Red de socios CNIB</p>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl flex items-center gap-2">
          <UsersIcon className="h-7 w-7 text-cnib-blue" /> Directorio de socios
        </h1>
        <p className="mt-1 text-sm text-slate-600">Busca, conecta y agrega a tus favoritos a socios cercanos.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar nombre, empresa o especialidad…"
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
            data-testid="directory-search"
          />
        </div>
        <select
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue"
          data-testid="directory-specialty"
        >
          <option value="">Todas las especialidades</option>
          {specs.map((s) => <option key={s.specialty_id || s.slug} value={s.slug}>{s.name}</option>)}
        </select>
        <select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue"
          data-testid="directory-city"
        >
          <option value="">Todas las ciudades</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-cnib-blue" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500" data-testid="directory-empty">
          No encontramos socios con esos criterios.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
          {items.map((m) => (
            <article key={m.user_id} className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg" data-testid={`directory-card-${m.user_id}`}>
              <div className="flex items-start gap-3 p-4">
                {m.picture ? (
                  <img src={m.picture} alt={m.name} className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-2 ring-orange-500/40" />
                ) : (
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-cnib-navy text-lg font-extrabold text-white">
                    {m.name?.charAt(0) || 'S'}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-extrabold text-slate-900">{m.name}</p>
                      {m.company && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">
                          <Briefcase className="h-3 w-3" /> {m.company}
                        </p>
                      )}
                      {m.city && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">
                          <MapPin className="h-3 w-3" /> {m.city}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleFav(m.user_id)}
                      data-testid={`directory-fav-${m.user_id}`}
                      className={`shrink-0 rounded-full p-1.5 transition-colors ${
                        favorites.has(m.user_id) ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-400 hover:text-red-500'
                      }`}
                      aria-label="Agregar a favoritos"
                    >
                      <Heart className={`h-4 w-4 ${favorites.has(m.user_id) ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>
              {Array.isArray(m.specialties) && m.specialties.length > 0 && (
                <div className="flex flex-wrap gap-1 px-4 pb-3">
                  {m.specialties.slice(0, 3).map((s, i) => (
                    <span key={i} className="rounded-full bg-cnib-blue/10 px-2 py-0.5 text-[10px] font-bold text-cnib-blue">{s}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
                <SocialRow m={m} />
                <Link
                  to={m.slug ? `/socio/${m.slug}` : '#'}
                  className="inline-flex items-center gap-1 text-xs font-bold text-cnib-blue hover:text-orange-500"
                  data-testid={`directory-view-${m.user_id}`}
                >
                  Ver perfil <Globe className="h-3 w-3" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function SocialRow({ m }) {
  const items = [
    m.whatsapp && { icon: Phone, href: `https://wa.me/${m.whatsapp.replace(/[^0-9]/g, '')}`, label: 'WhatsApp', cls: 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20' },
    m.email && { icon: Mail, href: `mailto:${m.email}`, label: 'Email', cls: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
    m.social_instagram && { icon: Instagram, href: m.social_instagram, label: 'Instagram', cls: 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/20' },
    m.social_facebook && { icon: Facebook, href: m.social_facebook, label: 'Facebook', cls: 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20' },
    m.social_linkedin && { icon: Linkedin, href: m.social_linkedin, label: 'LinkedIn', cls: 'bg-sky-500/10 text-sky-700 hover:bg-sky-500/20' },
    m.social_tiktok && { icon: Music2, href: m.social_tiktok, label: 'TikTok', cls: 'bg-slate-200 text-slate-800 hover:bg-slate-300' },
    m.social_youtube && { icon: Youtube, href: m.social_youtube, label: 'YouTube', cls: 'bg-red-500/10 text-red-600 hover:bg-red-500/20' },
    m.social_website && { icon: Globe, href: m.social_website, label: 'Sitio web', cls: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
  ].filter(Boolean);
  if (items.length === 0) return <span />;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {items.slice(0, 6).map((it, i) => (
        <a
          key={i}
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={it.label}
          title={it.label}
          className={`rounded-full p-1.5 transition-colors ${it.cls}`}
        >
          <it.icon className="h-3.5 w-3.5" />
        </a>
      ))}
    </div>
  );
}
